import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Gamepad2, BookOpen, ArrowRight } from 'lucide-react';

const DerivativesInteractiveBookCTA = ({ variant = 'default' }) => {
  if (variant === 'banner') {
    // Inline banner version for sidebars or page sections
    return (
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        borderRadius: '16px',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 140,
          height: 140,
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <Sparkles size={20} style={{ color: 'white' }} />
            <span style={{
              fontSize: '0.75rem',
              fontWeight: 800,
              color: 'white',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>NEW</span>
          </div>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: 800,
            color: 'white',
            marginBottom: '8px',
            lineHeight: 1.3
          }}>
            Derivatives Interactive Quick Study Guide
          </h3>
          <p style={{
            fontSize: '0.9rem',
            color: 'rgba(255,255,255,0.95)',
            marginBottom: '16px',
            lineHeight: 1.6
          }}>
            Master all 8 Derivatives elements plus mock exam with reading notes, games, flashcards, and quizzes. Was $129.99, now just $49.99 (March special).
          </p>
          <Link 
            to="/books/derivatives-interactive"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: 'white',
              color: '#7c3aed',
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '0.95rem',
              textDecoration: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Start Learning
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    );
  }

  // Default full-width section version
  return (
    <section style={{
      padding: '80px 0',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decorations */}
      <div style={{
        position: 'absolute',
        top: -100,
        left: -100,
        width: 300,
        height: 300,
        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
        borderRadius: '50%'
      }} />
      <div style={{
        position: 'absolute',
        bottom: -150,
        right: -150,
        width: 400,
        height: 400,
        background: 'radial-gradient(circle, rgba(236, 72, 153, 0.1) 0%, transparent 70%)',
        borderRadius: '50%'
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '48px',
          alignItems: 'center'
        }}>
          {/* Left content */}
          <div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 16px',
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '20px',
              marginBottom: '20px'
            }}>
              <Sparkles size={16} style={{ color: '#a78bfa' }} />
              <span style={{
                fontSize: '0.75rem',
                fontWeight: 800,
                color: '#a78bfa',
                letterSpacing: '0.05em',
                textTransform: 'uppercase'
              }}>Just Launched</span>
            </div>

            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 900,
              color: 'white',
              marginBottom: '16px',
              lineHeight: 1.2
            }}>
              Learn Derivatives Through<br />
              <span style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Interactive Games</span>
            </h2>

            <p style={{
              fontSize: '1.1rem',
              color: 'rgba(255,255,255,0.8)',
              lineHeight: 1.7,
              marginBottom: '24px'
            }}>
              Our revolutionary Derivatives Interactive Quick Study Guide makes exam prep engaging with comprehensive reading notes, visual learning, games, and instant feedback across all 8 elements plus a full-length mock exam.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '16px',
              marginBottom: '32px'
            }}>
              {[
                { icon: '🎮', label: 'Interactive', desc: 'Games & scenarios' },
                { icon: '📚', label: '8 Elements', desc: 'Full coverage' },
                { icon: '🎓', label: 'Mock Exam', desc: 'With diagnostics' },
                { icon: '💰', label: '$49.99', desc: 'Was $129.99' }
              ].map((item, idx) => (
                <div key={idx} style={{
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  padding: '16px',
                  textAlign: 'center',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <div style={{ fontSize: '1.75rem', marginBottom: '8px' }}>{item.icon}</div>
                  <div style={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: 'white',
                    marginBottom: '4px'
                  }}>{item.label}</div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: 'rgba(255,255,255,0.6)'
                  }}>{item.desc}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Link 
                to="/books/derivatives-interactive"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  textDecoration: 'none',
                  boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <BookOpen size={20} />
                Explore Interactive Book
                <ArrowRight size={20} />
              </Link>
              <Link 
                to="/books"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px 32px',
                  background: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  textDecoration: 'none',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                }}
              >
                View All Books
              </Link>
            </div>
          </div>

          {/* Right visual */}
          <div style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.05) 100%)',
              borderRadius: '24px',
              padding: '40px',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px'
              }}>
                {[
                  { emoji: '🏛️', color: '#8b5cf6' },
                  { emoji: '📊', color: '#ec4899' },
                  { emoji: '🎯', color: '#f59e0b' },
                  { emoji: '📈', color: '#10b981' },
                  { emoji: '🔄', color: '#3b82f6' },
                  { emoji: '🛡️', color: '#ef4444' },
                  { emoji: '🔢', color: '#14b8a6' },
                  { emoji: '⚖️', color: '#6366f1' },
                  { emoji: '🎓', color: '#d946ef' }
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: 70,
                      height: 70,
                      borderRadius: '16px',
                      background: `${item.color}20`,
                      border: `2px solid ${item.color}40`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      animation: `float ${3 + idx * 0.2}s ease-in-out infinite`,
                      animationDelay: `${idx * 0.1}s`
                    }}
                  >
                    {item.emoji}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </section>
  );
};

export default DerivativesInteractiveBookCTA;
